import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Bell,
  Send,
  Loader2,
  ChevronRight,
  PencilLine,
  User,
} from 'lucide-react';

const NotifyOperatorDialog = ({ open, onOpenChange, job, allocation, operators = [], onSent }) => {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewMessage, setPreviewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('select'); // 'select' | 'preview'
  const [isCustom, setIsCustom] = useState(false);

  // Determine the operator(s) to notify
  const targetOperators = operators.length > 0 ? operators : (allocation?.operator_id ? [{ id: allocation.operator_id, name: allocation.operator_name || 'Operator' }] : []);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/notification-templates');
      setTemplates(res.data.templates.filter(t => t.is_active));
    } catch (err) {
      toast.error('Failed to load templates');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setStep('select');
      setSelectedTemplate(null);
      setIsCustom(false);
      setPreviewTitle('');
      setPreviewMessage('');
    }
  }, [open, fetchTemplates]);

  const buildVariables = () => {
    const vars = {};
    if (job) {
      vars.job_number = job.job_number || job.id?.slice(-6) || '';
      vars.site_name = job.site_name || job.location || '';
      vars.customer_name = job.customer_name || job.client || '';
      vars.date = job.start_date || '';
      vars.start_time = job.start_time || '';
      vars.depot_time = job.depot_time || '';
      vars.description = job.description || '';
      vars.equipment_name = allocation?.equipment_name || '';
    }
    if (targetOperators.length === 1) {
      vars.operator_name = targetOperators[0].name || '';
    }
    return vars;
  };

  const resolveTemplate = (templateStr, variables) => {
    let result = templateStr;
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value || `{${key}}`);
    });
    return result;
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setIsCustom(false);
    const vars = buildVariables();
    setPreviewTitle(resolveTemplate(template.title_template, vars));
    setPreviewMessage(resolveTemplate(template.message_template, vars));
    setStep('preview');
  };

  const handleSelectCustom = () => {
    setSelectedTemplate(null);
    setIsCustom(true);
    setPreviewTitle('');
    setPreviewMessage('');
    setStep('preview');
  };

  const canSend = targetOperators.length > 0 && !sending &&
    (isCustom ? (previewTitle.trim() && previewMessage.trim()) : !!selectedTemplate);

  const handleSend = async () => {
    if (targetOperators.length === 0) return;
    if (!isCustom && !selectedTemplate) return;
    if (isCustom && (!previewTitle.trim() || !previewMessage.trim())) {
      toast.error('Enter a title and message');
      return;
    }

    setSending(true);
    try {
      if (isCustom) {
        // Free-form push — no template, no job-confirmation (ack) flow.
        await api.post('/notifications/push/send', {
          operator_ids: targetOperators.map(o => o.id),
          title: previewTitle,
          message: previewMessage,
          job_id: job?.id || null,
        });
      } else {
        const variables = buildVariables();
        await api.post('/notification-templates/send', {
          template_id: selectedTemplate.id,
          operator_ids: targetOperators.map(o => o.id),
          variables,
          job_id: job?.id || null,
        });
      }

      // Custom message = a standalone push only. It intentionally does NOT
      // trigger the job-confirmation (draft → sent) acknowledgement flow —
      // that lives on the "Notify Workers" action.
      toast.success(`Message sent to ${targetOperators.length} operator(s)`);
      onOpenChange(false);
      if (onSent) onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="notify-operator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#ffd108]" />
            Send Custom Message
          </DialogTitle>
          <DialogDescription>
            {step === 'select' 
              ? 'Pick a template or write your own message' 
              : 'Review and send the message'}
          </DialogDescription>
        </DialogHeader>

        {/* Recipients */}
        {targetOperators.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {targetOperators.map(op => (
              <Badge key={op.id} variant="secondary" className="text-xs">
                <User className="h-3 w-3 mr-1" />
                {op.name}
              </Badge>
            ))}
          </div>
        )}
        {targetOperators.length === 0 && (
          <p className="text-sm text-destructive">No operator assigned to this job. Allocate first.</p>
        )}

        {/* Step 1: Template Selection */}
        {step === 'select' && (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            <button
              onClick={handleSelectCustom}
              className="w-full text-left p-3 rounded-lg border border-[#ffd108]/40 bg-[#ffd108]/5 hover:border-[#ffd108] transition-colors group"
              data-testid="notify-custom-message-option"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm flex items-center gap-1.5">
                    <PencilLine className="h-4 w-4 text-[#ffd108]" /> Write your own message
                  </p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">Compose a free-form title and message</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-[#ffd108] flex-shrink-0 ml-2" />
              </div>
            </button>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-[#ffd108]" />
              </div>
            ) : templates.length === 0 ? (
              <p className="text-center py-6 text-muted-foreground text-sm">No active templates. Create one in Settings.</p>
            ) : (
              templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => handleSelectTemplate(t)}
                  className="w-full text-left p-3 rounded-lg border hover:border-[#ffd108]/50 hover:bg-[#ffd108]/5 transition-colors group"
                  data-testid={`notify-template-${t.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{t.message_template}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-[#ffd108] flex-shrink-0 ml-2" />
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && (selectedTemplate || isCustom) && (
          <div className="space-y-3">
            <button
              onClick={() => setStep('select')}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              &larr; Back
            </button>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2 border">
              <div>
                <Label className="text-xs text-muted-foreground">Title</Label>
                <Input
                  value={previewTitle}
                  onChange={e => setPreviewTitle(e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="Notification title"
                  data-testid="notify-preview-title"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Message</Label>
                <Textarea
                  value={previewMessage}
                  onChange={e => setPreviewMessage(e.target.value)}
                  rows={3}
                  className="mt-1 text-sm"
                  placeholder="Type your message"
                  data-testid="notify-preview-message"
                />
                <p className="text-[10px] text-muted-foreground mt-1">You can edit the title and message before sending</p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'preview' && (
            <Button 
              onClick={handleSend} 
              disabled={!canSend}
              data-testid="send-notification-button"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Send className="h-4 w-4 mr-1.5" />
              )}
              Send Message
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotifyOperatorDialog;
